/*
 * Membership page + shared "Use credits" offer card.
 * docs/membership-credits-spec.md (vault-generator, read-only from this repo) section 8
 * row 5. No number, tier name, credit count or day count is typed here or in any Liquid
 * file -- everything below reads it from a fetched JSON document instead.
 *
 * Contract 1 -- GET /apps/generator/pricing (real, already shipped -- services/orders/app.py
 * `get_pricing`, services/pricing/schema.py `PUBLIC_FIELDS`). Fetched once per page load,
 * never polled:
 *
 *   { version, currency, credit_value_cad, fx,
 *     tiers: { starter: {label, cad_month, credits_month, rollover_cap, signup_bonus, sku},
 *              classroom: {...}, school_year: {label, cad_term, credits_term, term_months,
 *                                               signup_bonus, sku, kind: "prepaid"} },
 *     topups: { "TOPUP-10": {cad, credits}, ... },
 *     expiry: { membership_grace_days, pause_freeze_days, topup_expires, topup_refund_days,
 *               bonus_follows_membership, reminder_days_before },
 *     free: { files, max_credits_per_file, daily_budget_files },
 *     assets: {...}, asset_bands: {...},
 *     catalogue: { unit_credits, half_unit_credits, course_credits, partial_pay },
 *     checkout: { credit_hold_minutes },
 *     restrictions: {...} }
 *
 * Contract 2 -- GET /apps/generator/credits (real route, extended response -- STUB, steps
 * 2/4 of the spec, not built yet). credit-meter.liquid's own script degrades this the same
 * way; documented again here because this file is the membership page's one script file:
 *
 *   { balance, unlimited, lifetime_spent,
 *     balance_unrestricted,                                    // NEW, absent until step 2
 *     grants_summary: { rolling, expiring, expires_at } | null  // NEW, absent until step 2
 *   }
 *
 *   Until grants_summary exists, the meter shows the balance alone and hides the expiry
 *   sentence -- see snippets/credit-meter.liquid, which owns that rendering.
 *
 * Contract 3 -- POST /apps/generator/credits/hold {sku, credits} -> 200 {code, expires_at,
 * checkout_url} (STUB, step 4 of the spec, not built yet). `TVCreditOffer` below is the one
 * binder for every "Use credits" button on the site (this page and the generator page's
 * catalogue-offer screen both load this file for it). On click: POST the hold; on 200,
 * navigate to checkout_url with the discount code applied; on anything else, show a plain
 * inline error and do not navigate.
 */
(function () {
  'use strict';

  function parseJsonSafe(res) {
    return res.text().then(function (t) {
      try { return t ? JSON.parse(t) : null; } catch (e) { return null; }
    });
  }

  function apiBaseFor(el) {
    var host = el && el.closest ? el.closest('[data-api-base]') : null;
    var base = (host && host.getAttribute('data-api-base')) || '/apps/generator';
    return base.replace(/\/+$/, '');
  }

  // ---- Contract 3: the "Use credits" offer card, shared by every page that has one ----

  var TVCreditOffer = {
    init: function (root) {
      var scope = root || document;
      scope.querySelectorAll('[data-credit-offer-button]').forEach(function (btn) {
        if (btn.getAttribute('data-credit-offer-bound')) return;
        btn.setAttribute('data-credit-offer-bound', 'true');
        btn.addEventListener('click', function () { TVCreditOffer.use(btn); });
      });
    },
    use: function (btn) {
      var card = btn.closest('[data-credit-offer]') || btn;
      var sku = card.getAttribute('data-sku') || btn.getAttribute('data-sku');
      var credits = parseInt(card.getAttribute('data-credits') || btn.getAttribute('data-credits'), 10);
      var errorEl = card.querySelector('[data-credit-offer-error]');
      if (errorEl) { errorEl.hidden = true; errorEl.textContent = ''; }
      if (!sku || isNaN(credits)) return;
      var apiBase = apiBaseFor(btn);
      btn.disabled = true;
      fetch(apiBase + '/credits/hold', {
        method: 'POST',
        headers: { 'Accept': 'application/json', 'Content-Type': 'application/json' },
        credentials: 'same-origin',
        body: JSON.stringify({ sku: sku, credits: credits })
      }).then(function (res) {
        return parseJsonSafe(res).then(function (body) { return { ok: res.ok, body: body }; });
      }).then(function (r) {
        btn.disabled = false;
        if (!r.ok || !r.body || !r.body.checkout_url) {
          if (errorEl) {
            errorEl.hidden = false;
            errorEl.textContent = 'Could not start checkout with credits just now. Please try again.';
          }
          return;
        }
        var url = String(r.body.checkout_url);
        if (r.body.code && url.indexOf('discount=') === -1) {
          url += (url.indexOf('?') === -1 ? '?' : '&') + 'discount=' + encodeURIComponent(r.body.code);
        }
        window.location.href = url;
      }).catch(function () {
        btn.disabled = false;
        if (errorEl) {
          errorEl.hidden = false;
          errorEl.textContent = 'Could not start checkout with credits just now. Please try again.';
        }
      });
    }
  };
  window.TVCreditOffer = TVCreditOffer;

  // ---- Contract 1: the membership page's own tiers / top-ups / FAQ ----

  var TIER_ORDER = ['starter', 'classroom', 'school_year'];

  // Join links (added 2026-09-25, Beau report -- nothing on the page could be clicked).
  // The product handle / variant ID for each tier comes from the section's own schema
  // settings, written onto data-* attributes by membership-page.liquid -- never typed here.
  // This function only builds the URL shape; it holds no product handle or variant ID.
  function joinUrlFor(key, tiersWrap) {
    if (!tiersWrap) return null;
    if (key === 'starter' || key === 'classroom') {
      var handle = tiersWrap.getAttribute('data-membership-product-handle');
      if (!handle) return null;
      var variantId = tiersWrap.getAttribute(
        key === 'starter' ? 'data-starter-variant-id' : 'data-classroom-variant-id'
      );
      var url = '/products/' + handle;
      if (variantId) url += '?variant=' + encodeURIComponent(variantId);
      return url;
    }
    if (key === 'school_year') {
      var syHandle = tiersWrap.getAttribute('data-school-year-product-handle');
      return syHandle ? '/products/' + syHandle : null;
    }
    return null;
  }

  // Same idea for the three top-up chips, keyed by the pricing document's own SKU.
  var TOPUP_HANDLE_ATTR = {
    'TOPUP-10': 'data-topup-10-handle',
    'TOPUP-25': 'data-topup-25-handle',
    'TOPUP-50': 'data-topup-50-handle'
  };

  function topupUrlFor(sku, topupsWrap) {
    var attr = TOPUP_HANDLE_ATTR[sku];
    if (!attr || !topupsWrap) return null;
    var handle = topupsWrap.getAttribute(attr);
    return handle ? '/products/' + handle : null;
  }

  function money(n, currency) {
    if (typeof n !== 'number') return '';
    var amount = n.toFixed(2);
    return (currency === 'CAD' || !currency) ? '$' + amount : '$' + amount + ' ' + currency;
  }

  // Spec 6.2, one line per tier, verbatim templates -- only the numbers are substituted.
  function tierLine(tier, expiry) {
    var days = expiry && expiry.membership_grace_days;
    if (tier.kind === 'prepaid') {
      return tier.credits_term + ' credits for ten months from the day you join. '
        + 'Unspent credits expire ' + days + ' days after the term ends.';
    }
    return tier.credits_month + ' credits a month. Unused credits carry over, up to '
      + tier.rollover_cap + '. Credits expire ' + days + ' days after your membership ends.';
  }

  function tierPriceLine(tier, currency) {
    if (tier.kind === 'prepaid') {
      var termLabel = tier.term_months ? (tier.term_months + '-month term') : 'school year';
      return money(tier.cad_term, currency) + ' / ' + termLabel;
    }
    return money(tier.cad_month, currency) + ' / month';
  }

  // ---- UX review 3 Oct, item 4: cost per credit, saving, and what a credit buys ----
  // Every number is computed from the pricing document: the base rate is
  // credit_value_cad (a dollar of store value), each card's rate is its own price over its
  // own credits. Nothing here holds a price.

  function creditValue(cad, credits, pricing) {
    var base = Number(pricing && pricing.credit_value_cad);
    cad = Number(cad); credits = Number(credits);
    if (!(cad > 0) || !(credits > 0) || !(base > 0)) return null;
    var per = cad / credits;
    var save = Math.round((1 - per / base) * 100);
    return { per: per, save: save > 0 ? save : 0 };
  }

  // "$0.83 per credit" + a "Save 17%" chip, or "Standard rate" when there is no saving.
  function fillValue(el, cad, credits, pricing) {
    if (!el) return;
    var v = creditValue(cad, credits, pricing);
    el.textContent = '';
    if (!v) { el.hidden = true; return; }
    el.appendChild(document.createTextNode(money(Math.round(v.per * 100) / 100, pricing.currency) + ' per credit'));
    var chip = document.createElement('span');
    chip.className = 'membership-page__value-save';
    chip.textContent = v.save > 0 ? 'Save ' + v.save + '%' : 'Standard rate';
    el.appendChild(chip);
    el.hidden = false;
  }

  // A ready-made (library) file costs its generated price times the library factor,
  // rounded up, never under the minimum -- the same rule helper-drawer.js and
  // lesson-search.js apply, so the tile and the button always agree.
  function libraryCredits(pricing, asset) {
    var base = pricing && pricing.assets && Number(pricing.assets[asset]);
    var rule = pricing && pricing.catalogue && pricing.catalogue.library_file;
    if (!(base > 0) || !rule || !(Number(rule.factor) > 0) || !(Number(rule.min_credits) > 0)) return null;
    return Math.max(Number(rule.min_credits), Math.ceil(base * Number(rule.factor) - 1e-9));
  }

  function creditsLabel(n) {
    return n + (n === 1 ? ' credit' : ' credits');
  }

  function hydrateBuys(pricing) {
    var wrap = document.querySelector('[data-membership-buys]');
    if (!wrap) return;
    var assets = pricing.assets || {};
    var catalogue = pricing.catalogue || {};
    var shown = 0;
    function set(key, cost, line) {
      var tile = wrap.querySelector('[data-buy="' + key + '"]');
      if (!tile) return;
      if (cost == null || cost === '') { tile.hidden = true; return; }
      var costEl = tile.querySelector('[data-buy-cost]');
      if (costEl) costEl.textContent = cost;
      if (line) {
        var lineEl = tile.querySelector('[data-buy-line]');
        if (lineEl) lineEl.textContent = line;
      }
      shown += 1;
    }
    var ready = libraryCredits(pricing, 'worksheet');
    set('ready', ready != null ? creditsLabel(ready) : null);
    var custom = Number(assets.worksheet);
    set('custom', custom > 0 ? creditsLabel(custom) : null);
    var planReady = libraryCredits(pricing, 'lesson-plan');
    var planOwn = Number(assets['lesson-plan']);
    set('lesson-plan', planReady != null ? creditsLabel(planReady) : null,
      planOwn > 0 ? 'Ready-made, with the slide deck. Your own version: ' + creditsLabel(planOwn) + '.' : null);
    var unit = Number(catalogue.unit_credits);
    var unitTile = wrap.querySelector('[data-buy="unit"]');
    var cash = unitTile ? unitTile.getAttribute('data-unit-cash') : '';
    set('unit', unit > 0 ? creditsLabel(unit) + (cash ? ' or ' + cash : '') : null);
    wrap.hidden = shown === 0;
  }

  function hydrateTiers(pricing) {
    var wrap = document.querySelector('[data-membership-tiers]');
    if (!wrap) return;
    var recommended = wrap.getAttribute('data-recommended-tier');
    TIER_ORDER.forEach(function (key) {
      var tier = pricing.tiers && pricing.tiers[key];
      var card = wrap.querySelector('[data-tier="' + key + '"]');
      if (!tier || !card) return;
      var label = card.querySelector('[data-tier-label]');
      var price = card.querySelector('[data-tier-price]');
      var line = card.querySelector('[data-tier-line]');
      if (label) label.textContent = tier.label || key;
      if (price) price.textContent = tierPriceLine(tier, pricing.currency);
      if (line) line.textContent = tierLine(tier, pricing.expiry);
      if (tier.kind === 'prepaid') {
        fillValue(card.querySelector('[data-tier-value]'), tier.cad_term, tier.credits_term, pricing);
      } else {
        fillValue(card.querySelector('[data-tier-value]'), tier.cad_month, tier.credits_month, pricing);
      }
      var badge = card.querySelector('[data-tier-badge]');
      var isRecommended = recommended === key;
      if (badge) badge.hidden = !isRecommended;
      card.classList.toggle('membership-page__tier--recommended', isRecommended);
      var cta = card.querySelector('[data-tier-cta]');
      if (cta) {
        var joinUrl = joinUrlFor(key, wrap);
        if (joinUrl) {
          cta.setAttribute('href', joinUrl);
        } else {
          cta.hidden = true;
        }
      }
      card.hidden = false;
    });
  }

  // Spec 6.2's top-ups line is the same for every pack and carries no number:
  // "Top-up credits never expire."
  function hydrateTopups(pricing) {
    var wrap = document.querySelector('[data-membership-topups]');
    var list = document.querySelector('[data-membership-topups-list]');
    var line = document.querySelector('[data-membership-topups-line]');
    if (line) line.textContent = 'Top-up credits never expire.';
    if (!list || !pricing.topups) return;
    list.innerHTML = '';
    // Cheapest first, whatever order the pricing document's keys arrive in
    // (the live document served $25, $50, $10 -- Beau, 2026-09-25).
    var skus = Object.keys(pricing.topups).sort(function (a, b) {
      return Number(pricing.topups[a].cad) - Number(pricing.topups[b].cad);
    });
    skus.forEach(function (sku) {
      var pack = pricing.topups[sku];
      var item = document.createElement('li');
      item.className = 'membership-page__topup';
      var amount = document.createElement('span');
      amount.className = 'membership-page__topup-amount';
      amount.textContent = money(pack.cad, pricing.currency);
      var credits = document.createElement('span');
      credits.className = 'membership-page__topup-credits';
      credits.textContent = pack.credits + ' credits';
      item.appendChild(amount);
      item.appendChild(credits);
      var value = document.createElement('p');
      value.className = 'membership-page__value';
      fillValue(value, pack.cad, pack.credits, pricing);
      item.appendChild(value);
      // Same gold primary button as the tier cards' "Join ..." (1 Oct, Beau). No link,
      // no button -- a card that cannot be bought from says nothing it cannot do.
      var url = topupUrlFor(sku, wrap);
      if (url) {
        var link = document.createElement('a');
        link.className = 'membership-page__topup-link button button--primary button--full';
        link.setAttribute('href', url);
        link.textContent = 'Buy ' + pack.credits + ' credits';
        item.appendChild(link);
      }
      list.appendChild(item);
    });
  }

  // Spec 6.7, six items. Two carry a number (membership_grace_days); one more carries a
  // number in its own question title (the free-file count) -- every number below is read
  // from `pricing`, never written here.
  function hydrateFaq(pricing) {
    var root = document.querySelector('[data-membership-faq]');
    if (!root) return;
    var days = pricing.expiry && pricing.expiry.membership_grace_days;
    var freeFiles = pricing.free && pricing.free.files;
    var entries = {
      'what-is-a-credit': {
        q: 'What is a credit?',
        a: 'One credit is one dollar of store value. Spend credits on generated materials, units or courses.'
      },
      'do-credits-expire': {
        q: 'Do credits expire?',
        a: 'Membership credits carry over up to a cap and expire ' + days + ' days after your membership ends. Top-up credits never expire.'
      },
      'spent-first': {
        q: 'Which credits are spent first?',
        a: 'The ones closest to expiring.'
      },
      'buy-with-credits': {
        q: 'Can I buy a unit or course with credits?',
        a: 'Yes. Use credits for the full price of any file, unit, or course.'
      },
      'if-i-cancel': {
        q: 'What happens if I cancel?',
        a: 'You keep your files. Unspent membership credits expire ' + days + ' days after your last paid cycle.'
      },
      'free-materials': {
        q: 'What are the ' + freeFiles + ' free materials?',
        a: 'New accounts can generate ' + freeFiles + ' materials free; after that, every material costs credits.'
      }
    };
    Object.keys(entries).forEach(function (key) {
      var item = root.querySelector('[data-faq="' + key + '"]');
      if (!item) return;
      var q = item.querySelector('[data-faq-q]');
      var a = item.querySelector('[data-faq-a]');
      if (q) q.textContent = entries[key].q;
      if (a) a.textContent = entries[key].a;
      item.hidden = false;
    });
  }

  // The real "Use credits" button lives on the generator page's catalogue-offer card
  // (speedy-facts-generator.liquid), against a real unit and its live price -- this page
  // does not repeat it against a made-up SKU (review fix, 2026-09-24).

  function hydrateAll(pricing) {
    hydrateBuys(pricing);
    hydrateTiers(pricing);
    hydrateTopups(pricing);
    hydrateFaq(pricing);
  }

  function fetchPricingOnce(apiBase) {
    return fetch(apiBase + '/pricing', {
      method: 'GET',
      headers: { 'Accept': 'application/json' },
      credentials: 'same-origin'
    }).then(function (res) {
      return res.ok ? parseJsonSafe(res) : null;
    }).catch(function () { return null; });
  }

  // GET /pricing failed outright: say so in one plain line instead of leaving the tiers,
  // top-ups and FAQ sitting hidden with nothing to explain why. The tiers/top-ups/FAQ
  // markup stays hidden either way -- this only adds the explanation on a real failure.
  function showPricingError() {
    var errorEl = document.querySelector('[data-membership-load-error]');
    if (errorEl) errorEl.hidden = false;
  }

  function init() {
    TVCreditOffer.init(document);
    var membershipRoot = document.querySelector('[data-membership-page]');
    if (!membershipRoot) return; // this page has no tiers/top-ups/FAQ to hydrate
    var apiBase = apiBaseFor(membershipRoot);
    fetchPricingOnce(apiBase).then(function (pricing) {
      if (!pricing) { showPricingError(); return; }
      hydrateAll(pricing);
    });
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();
